import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedIcon from '@mui/icons-material/Verified';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { format } from 'date-fns';
import { getReviews, addReviewReply, updateReviewReply, deleteReviewReply } from '@services/reviewService';
import { getStylists } from '@services/stylistService';
import { toast } from 'react-toastify';
import StylistSelector from '@components/StylistSelector';
import type { Review, ReviewReply } from '@app-types/models/review';
import type { Stylist } from '@app-types/models/stylist';
import { PageContainer, PageHeader, SectionCard, EmptyState, LoadingState } from '../components/ui';
import { brand } from '../theme/tokens';

interface SelectedReview extends Review {
  reply: ReviewReply;
}

interface ApiError {
  message?: string;
}

const ReviewsPageNew: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [selectedReview, setSelectedReview] = useState<SelectedReview | null>(null);
  const [replyTexts, setReplyTexts] = useState<Record<string, string>>({});
  const [editDialogText, setEditDialogText] = useState<string>('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [selectedStylist, setSelectedStylist] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needsAttention'>('all');
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingReviews, setIsLoadingReviews] = useState<boolean>(false);

  useEffect(() => {
    const fetchStylists = async (): Promise<void> => {
      try {
        const stylistsResult = await getStylists();
        const stylistsData = Array.isArray(stylistsResult) ? stylistsResult : [];
        setStylists(stylistsData);
        if (stylistsData.length > 0) {
          setSelectedStylist(stylistsData[0]._id);
        }
      } catch (error: unknown) {
        console.error('Error fetching stylists:', error);
        toast.error(t('common.errorLoadingData'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStylists();
  }, [t]);

  useEffect(() => {
    const fetchReviews = async (): Promise<void> => {
      if (!selectedStylist) return;

      try {
        setIsLoadingReviews(true);
        const reviewsResponse = await getReviews(selectedStylist);
        setReviews(Array.isArray(reviewsResponse.reviews) ? reviewsResponse.reviews : []);
      } catch (error: unknown) {
        console.error('Error fetching reviews:', error);
        toast.error(t('common.errorLoadingData'));
      } finally {
        setIsLoadingReviews(false);
      }
    };

    fetchReviews();
  }, [selectedStylist, t]);

  const handleStylistSelect = (stylistId: string): void => {
    setSelectedStylist(stylistId);
  };

  const handleSubmitReply = async (reviewId: string): Promise<void> => {
    if (!replyTexts[reviewId]?.trim()) return;
    try {
      await addReviewReply({ reviewId, reply: replyTexts[reviewId], staffId: selectedStylist! });
      const updatedReviewsResponse = await getReviews(selectedStylist!);
      setReviews(Array.isArray(updatedReviewsResponse.reviews) ? updatedReviewsResponse.reviews : []);
      setReplyTexts(prev => {
        const newTexts = { ...prev };
        delete newTexts[reviewId];
        return newTexts;
      });
      toast.success(t('reviews.replyAdded'));
    } catch (error: unknown) {
      toast.error((error as ApiError).message || t('reviews.errorAddingReply'));
    }
  };

  const handleSubmitEdit = async (): Promise<void> => {
    if (!editDialogText?.trim()) return;
    try {
      await updateReviewReply({ reviewId: selectedReview!._id, replyId: selectedReview!.reply._id, reply: editDialogText, staffId: selectedStylist! });
      const updatedReviewsResponse2 = await getReviews(selectedStylist!);
      setReviews(Array.isArray(updatedReviewsResponse2.reviews) ? updatedReviewsResponse2.reviews : []);
      setIsEditDialogOpen(false);
      setEditDialogText('');
      toast.success(t('reviews.replyUpdated'));
    } catch (error: unknown) {
      toast.error((error as ApiError).message || t('reviews.errorUpdatingReply'));
    }
  };

  const handleDeleteReply = async (reviewId: string, replyId: string): Promise<void> => {
    if (window.confirm(t('reviews.confirmDeleteReply'))) {
      try {
        await deleteReviewReply({ reviewId, replyId, staffId: selectedStylist! });
        const updatedReviewsResponse3 = await getReviews(selectedStylist!);
        setReviews(Array.isArray(updatedReviewsResponse3.reviews) ? updatedReviewsResponse3.reviews : []);
        toast.success(t('reviews.replyDeleted'));
      } catch (error: unknown) {
        toast.error((error as ApiError).message || t('reviews.errorDeletingReply'));
      }
    }
  };

  const handleEditReply = (review: Review, reply: ReviewReply): void => {
    setSelectedReview({ ...review, reply });
    setEditDialogText(reply.content);
    setIsEditDialogOpen(true);
  };

  const handleReplyTextChange = (reviewId: string, value: string): void => {
    setReplyTexts(prev => ({
      ...prev,
      [reviewId]: value
    }));
  };

  const handleEditDialogTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setEditDialogText(e.target.value);
  };

  const needsAttentionCount = reviews.filter((review) => review.status === 'pending-recovery').length;
  const displayedReviews = reviewFilter === 'needsAttention'
    ? reviews.filter((review) => review.status === 'pending-recovery')
    : reviews;

  // --- Presentation helpers ---
  const renderReplyComposer = (reviewId: string) => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ mt: 2 }}
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
    >
      <TextField
        size="small"
        placeholder={t('reviews.replyPlaceholder')}
        fullWidth
        value={replyTexts[reviewId] || ''}
        onChange={(e) => handleReplyTextChange(reviewId, e.target.value)}
        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmitReply(reviewId);
          }
        }}
      />
      <Button
        variant="contained"
        onClick={() => handleSubmitReply(reviewId)}
        disabled={!replyTexts[reviewId]?.trim()}
        sx={{ minWidth: { sm: 100 }, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
      >
        {t('reviews.reply')}
      </Button>
    </Stack>
  );

  return (
    <PageContainer>
      <PageHeader
        title={t('reviews.title', 'Reviews')}
        subtitle={t('reviews.subtitleNew', 'Read what customers are saying and reply to build trust.')}
        icon={<RateReviewIcon />}
        classicPath="/classic/reviews"
      />

      {isLoading ? (
        <LoadingState variant="list" />
      ) : (
        <>
          <SectionCard
            title={t('reviews.filtersTitle', 'Filters')}
            sx={{ mb: 2.5 }}
          >
            <Stack spacing={2}>
              <StylistSelector
                stylists={stylists}
                selectedStylistId={selectedStylist ?? undefined}
                onStylistSelect={(value) => handleStylistSelect(value as string)}
                layout="scroll"
                showEmail={true}
                variant="id"
              />

              <ToggleButtonGroup
                value={reviewFilter}
                exclusive
                onChange={(_, value) => value && setReviewFilter(value)}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="all" sx={{ minHeight: 40 }}>
                  {t('reviews.filterAll')}
                </ToggleButton>
                <ToggleButton value="needsAttention" sx={{ minHeight: 40 }}>
                  {t('reviews.filterNeedsAttention')} ({needsAttentionCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </SectionCard>

          {isLoadingReviews ? (
            <LoadingState variant="list" />
          ) : displayedReviews.length === 0 ? (
            <SectionCard>
              <EmptyState
                icon={<RateReviewIcon />}
                title={t('reviews.emptyTitle', 'No reviews yet')}
                description={
                  reviewFilter === 'needsAttention'
                    ? t('reviews.emptyNeedsAttentionDescription', 'Nothing needs your attention right now — every review has been handled.')
                    : t('reviews.emptyDescription', "Once customers leave reviews for this stylist, they'll appear here so you can reply.")
                }
              />
            </SectionCard>
          ) : (
            <Stack spacing={2}>
              {displayedReviews.map((review) => (
                <Card key={review._id}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {/* Header: author + rating + reservation preview */}
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar sx={{ bgcolor: brand.gold }}>
                          {review?.user?.displayName?.charAt(0)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                            {review?.user?.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(review?.createdAt ?? ''), 'PPp')}
                          </Typography>
                        </Box>
                      </Stack>

                      {review.reservationId && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'flex-start', sm: 'flex-end' },
                            backgroundColor: brand.cream,
                            border: `1px solid ${brand.border}`,
                            p: 1.5,
                            borderRadius: 2,
                            width: { xs: '100%', sm: 'auto' },
                            maxWidth: { sm: 260 },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {t('reservation.reservedSlot')}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {format(new Date(review.reservationId.start), 'PPp')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {i18n.language === 'zh' ? review.reservationId.planId.zh_name : review.reservationId.planId.name}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Rating + status chips */}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 1.5, mb: 1 }}
                    >
                      <Rating value={review.rating} readOnly />
                      {review.verifiedVisit && (
                        <Chip
                          icon={<VerifiedIcon />}
                          label={t('reviews.verifiedVisit')}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      {review.status === 'pending-recovery' && (
                        <Chip
                          label={t('reviews.needsAttentionBadge')}
                          size="small"
                          color="warning"
                        />
                      )}
                    </Stack>

                    <Typography variant="body1" sx={{ mb: review.highlights && review.highlights.length > 0 ? 1.5 : 0 }}>
                      {review.comment}
                    </Typography>

                    {review.highlights && review.highlights.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        {review.highlights.map((highlight, index) => (
                          <Chip
                            key={index}
                            label={highlight}
                            size="small"
                            sx={{ bgcolor: brand.goldSoft }}
                          />
                        ))}
                      </Stack>
                    )}

                    {/* Reply area */}
                    <Box
                      sx={{
                        mt: 2,
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 2,
                        bgcolor: brand.cream,
                        border: `1px solid ${brand.border}`,
                      }}
                    >
                      {review.replies && review.replies.length > 0 && (
                        <Stack spacing={1.5} sx={{ mb: 1 }}>
                          {review.replies.map((reply) => (
                            <Box key={reply._id}>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {reply.user ? t('reviews.userReply') : t('reviews.yourReply')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {format(new Date(reply?.updatedAt ?? ''), 'PPp')}
                                </Typography>
                                {!reply.user && (
                                  <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
                                    <Tooltip title={t('common.edit', 'Edit')}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditReply(review, reply)}
                                        sx={{ width: 40, height: 40 }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('common.delete', 'Delete')}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteReply(review._id, reply._id)}
                                        sx={{ width: 40, height: 40 }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </Stack>
                              <Typography variant="body2" sx={{ mt: 0.25 }}>{reply.content}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                      {renderReplyComposer(review._id)}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Edit Reply Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('reviews.editReply')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('reviews.replyText')}
            fullWidth
            multiline
            rows={4}
            value={editDialogText}
            onChange={handleEditDialogTextChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            disabled={!editDialogText?.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ReviewsPageNew;
